import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ChevronRight } from "lucide-react";
import { SectionTitle, GlassCard } from "../components/common/ui";
import ProductCard from "../components/common/ProductCard";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import ContentSections from "../components/public/ContentSections";
import RelatedContent from "../components/public/RelatedContent";
import api from "../lib/api";

export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [items, setItems] = useState([]);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false); setRelated(null);
    api.get(`/categories/${slug}`)
      .then(async ({ data }) => {
        if (data.slug && data.slug !== slug) { navigate(`/fireworks/${data.slug}`, { replace: true }); return; }
        setCat(data);
        const { data: prods } = await api.get("/products", { params: { category_id: data.category_id, limit: 48 } });
        setItems(prods.items);
        api.get("/related-content", { params: { entity_type: "category", slug: data.slug } }).then(({ data: rc }) => setRelated(rc)).catch(() => {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (notFound) return (
    <div className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="category-not-found">
      <h1 className="font-playfair text-3xl text-white">Category not found</h1>
      <Link to="/fireworks" className="mt-4 inline-block text-gold underline">Browse all fireworks</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid="category-page">
      <Breadcrumbs className="mb-6" />
      <SectionTitle eyebrow="Category" title={cat.name} subtitle={cat.description || cat.short_description} />
      <div className="mt-10">
        {items.length === 0 ? (
          <GlassCard className="p-12 text-center text-muted-foreground" hover={false}>No products in this category yet.</GlassCard>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="category-grid">
            {items.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </div>
      <ContentSections content={cat.content_seo} />
      <RelatedContent data={related} />
    </div>
  );
}
